import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps } from "../Button";
import { useEffect, useRef } from "react";
import { Draw } from "ol/interaction";
import { useMap } from "../../../hooks";
import Style from "ol/style/Style";
import { DrawEvent, createBox } from "ol/interaction/Draw";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import { RectangleIcon } from "../../../constants/icons/RectangleIcon";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { useCallback } from "react";
import { ANNOTATION_COLOR } from "../../../constants";
import { FeatureFromGeojson, useFeaturesStore } from "../../../FeaturesStore";
import { positionsFromFeature } from "../../../../utils";
import { makeGeojsonShape } from "../../../../utils/makeGeojsonShape";
import { Coordinate } from "ol/coordinate";
import {
  useDrawSketchGestures,
  primaryPointerCondition,
} from "src/domains/shared/react-openlayers/Map/hooks/useDrawSketchGestures";
import { useToolGuide } from "../../useToolGuide";
import {
  DRAWING_FLAG_RELEASE_DELAY_MS,
  DRAWING_TOOL_CURSOR,
  MAP_TOOL_BUTTON,
  MAP_TOOL_LABEL_KEYS,
} from "src/domains/shared/constants/mapToolButton";
import { releaseDrawToolUi } from "./releaseDrawToolUi";

export interface RectangleDrawButtonProps extends ButtonProps {
  /**
   * @description You can get Multipoint feature what was made by callback function.
   */
  onEnd?: (features: FeatureFromGeojson) => void;

  /**
   * @description You can set callback Fn on 'start' event.
   */
  onStart?: () => void;
  /**
   * @default false
   * @description Well... Sometimes you need this drawing tool with using server waht containes DB. if 'onCanvas' set false, react-openlayer will not draw feature on canvas.
   */
  onCanvas?: boolean;
  color?: keyof typeof ANNOTATION_COLOR;
}

export function RectangleDrawButton({
  onEnd,
  onClick,
  onCanvas = false,
  onStart,
  color = "BLUE",
  disabled,
  ...props
}: RectangleDrawButtonProps) {
  const { t } = useTranslation("timeSeries");
  const map = useMap();
  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();

  const isActive = buttonId === selectedButtonId;
  const { addGeoJson } = useFeaturesStore();
  const createDraw = useCallback(
    () =>
      new Draw({
      condition: primaryPointerCondition,
        source: undefined,
        type: "Circle",
        geometryFunction: createBox(),
        style: new Style({
          zIndex: 1000,
          stroke: new Stroke({
            color: ANNOTATION_COLOR[color].stroke(1),
            width: 2,
          }),
          fill: new Fill({
            color: ANNOTATION_COLOR[color].fill(1),
          }),
        }),
      }),
    [color]
  );

  const drawRef = useRef(createDraw());

  useEffect(() => {
    drawRef.current = createDraw();
  }, [onCanvas, color, createDraw]);

  const exitDrawing = useCallback(() => {
    releaseDrawToolUi(map, drawRef.current, selectButton);
    map.setProperties({ isDrawing: false });
  }, [map, selectButton]);

  const { pointCount } = useDrawSketchGestures({
    map,
    isActive,
    kind: "rectangle",
    getDraw: () => drawRef.current,
    onExit: exitDrawing,
  });
  useToolGuide({ isActive, kind: "rectangle", pointCount });

  const startDrawing = () => {
    if (onClick) {
      onClick();
    }

    if (onStart) {
      onStart();
    }
    map.getViewport().style.cursor = DRAWING_TOOL_CURSOR;
    map.setProperties({ isDrawing: true });
    map.addInteraction(drawRef.current);
  };

  const drawing = useCallback(
    (event: DrawEvent) => {
      const feature = event.feature;

      const newPosition = positionsFromFeature(feature, true) as Coordinate[][];

      const newGeoJson = makeGeojsonShape(
        {
          type: "Polygon",
          coordinates: newPosition,
        },
        {
          type: "rectangle",
          original_type: "rectangle",
          isSelected: true,
        }
      );

      releaseDrawToolUi(map, drawRef.current, selectButton);

      if (onEnd) {
        onEnd(newGeoJson);
      }
      if (onCanvas) {
        addGeoJson(newGeoJson);
      }
      setTimeout(
        () => map.setProperties({ isDrawing: false }),
        DRAWING_FLAG_RELEASE_DELAY_MS
      );
    },
    [selectButton, map, onEnd, onCanvas, addGeoJson]
  );

  useEffect(() => {
    const drawingInstance = drawRef.current;

    drawingInstance.on("drawend", drawing);
    return () => {
      drawingInstance.un("drawend", drawing);
    };
  }, [drawing]);

  useEffect(() => {
    if (!isActive) {
      map.removeInteraction(drawRef.current);
    }
  }, [isActive, map]);

  return (
    <Button
      id={buttonId}
      hasPopup
      disabled={disabled}
      isDisabled={disabled}
      popupText={t(MAP_TOOL_LABEL_KEYS.rectangle)}
      onClick={() => {
        if (isActive) {
          drawRef.current.abortDrawing();
          exitDrawing();
        } else {
          selectButton(buttonId);
          startDrawing();
        }
      }}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <RectangleIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
}
